import React, { useState, useContext } from 'react'
import withApolloProvider from 'components/hocs/withApolloProvider'
import withReduxProvider from 'components/hocs/withReduxProvider'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { compose, bindActionCreators } from 'redux'
import pickby from 'lodash.pickby'
import Tooltip from 'components/tooltip'
import identity from 'lodash.identity'
import { Collapse } from 'react-collapse'
import { AdvertiserDisclosure } from 'components/advertiser-disclosure'
import { useViewport } from 'components/hooks'
import Font from '@cred/react-font'
import { Testable } from '@cred/react-testable'
import { collapsibleClick } from '@cred/react-analytics/events'
import { withSnowplowContexts } from '@cred/react-analytics/SnowplowContextManager'
import { withStyles } from '@cred/react-theme'
import { extractNumber } from '@cred/shared/utils'

import { getMetaQuery } from 'utils/data'
import { setSubmission as setSubmissionAction } from 'utils/redux/submission/action'
import CompareRates from './compare-rates'
import FilterForm from './filter-form'
import useProductsAndFormHandler from './shared/hooks/useProductsAndFormHandler'
import SortingForm from './sorting-form'
import TabsForm from './tabs-form'
import RateWidgetFooter from './footer'
import defaultStyles from '../themes/default.module.scss'
import fox1 from '../themes/fox.module.scss'
import homes1 from '../themes/homes1.module.scss'
import insider from '../themes/insider.module.scss'
import InitialValuesContext from '../../../components/initial-values-context'

import {
  PURCHASE_TYPE,
  REFINANCE_TYPE,
  MORTGAGE_FILTER_FORM_NAME,
  CASH_OUT_LIMIT_INDEX,
} from './common/constants'
import {
  generateOnGetStartedCallback,
  getFilterText,
  mortgageWidgetMapStateToProps,
} from './common/utils'

const paginationName = 'mortgage-rate-table'
const LENDER_PER_PAGE = 5
const Widget = props => {
  const {
    variation,
    analyticsContexts,
    trackSnowplowEvent,
    formData,
    /* eslint-disable-next-line */
    utms: { utm_campaign, utm_source, utm_medium, utm_content },
    initialValues,
    partnerFormData,
    sortBy,
    termState,
    loanTypeState,
    isPurchase,
    styles,
    theme,
    omitUtmParams,
    page,
    metaParams,
    verticals,
    type,
    actions: { setSubmission },
  } = props

  const { activeTab } = useContext(InitialValuesContext) || {}
  const [showDisclosure] = useState(['fox1', 'thebalance1', 'investopedia1'].includes(theme))

  const metaQuery = getMetaQuery({ ...metaParams, marketplace: verticals, type }, theme)

  const [submitFormValues, formValues, currentProducts, loadingProducts, error] = useProductsAndFormHandler({ initialValues, partnerFormData, setSubmission }),
    [isOpen, setIsOpen] = useState(false),
    isFullTable = variation !== 'shortened',
    termFilters = Object.entries(termState)
      .filter(v => v[1])
      .map(v => v[0]),
    utmParams = pickby(
      {
        utm_source,
        utm_medium,
        utm_campaign,
        utm_content,
      },
      identity,
    ),
    onGetStarted = generateOnGetStartedCallback({ utmParams, isPurchase, omitUtmParams, metaQuery }),
    { sorting } = initialValues

  // ARM disabled tmp
  const products = (currentProducts[loanTypeState] || []).filter(
      ({ productType }) => productType !== 'fiveOneARM',
    ),
    filteredProducts = termFilters.length
      ? products.filter(({ productType }) => termFilters.some(e => e === productType))
      : products,
    sortedProducts = filteredProducts.sort((p1, p2) => p1[sortBy] - p2[sortBy]),
    bestProducts = filteredProducts.filter(product => product.bestProduct),
    sortedBestProduct = bestProducts.sort((p1, p2) => p1[sortBy] - p2[sortBy]),
    toggleCollapse = () => {
      setIsOpen(!isOpen)
      trackSnowplowEvent(collapsibleClick, {
        text: 'Edit icon',
        action: isOpen ? 'collapse' : 'expand',
      })
    },
    editButton = opened => (
      <Testable className={styles.captureHeader} onClick={toggleCollapse} testId="header-capture">
        <Font className={styles.captureTitle} type="body">
          {getFilterText(formValues, termState, isPurchase)}
        </Font>
        <i
          className={`${styles.editButton} ${
            opened ? styles.editButtonOpened : ''
          } cred-icon icon-edit`}
        />
      </Testable>
    )

  const isDesktop = useViewport() > 1024
  const { values: formDataValues } = formData || {}
  const { cashOut, homeValue, loanAmount, downPayment, estimatedValue } = formDataValues || {}
  const isCashOutError = !isPurchase && extractNumber(cashOut) / extractNumber(homeValue) >= CASH_OUT_LIMIT_INDEX
  const isValidationsError = isPurchase ? extractNumber(downPayment) >= extractNumber(estimatedValue) : extractNumber(loanAmount) >= extractNumber(homeValue)
  const isErrorState = isCashOutError || isValidationsError
  const filterFormProps = {
    analyticsContexts,
    loadingProducts,
    initialValues: formValues,
    form: MORTGAGE_FILTER_FORM_NAME,
    onSubmit: () => isCashOutError || isValidationsError ? null : submitFormValues(formData),
    isCashOutError,
    isValidationsError,
    formData,
  }
  const paginationProps = {
    paginationName,
    variation,
    page,
    LENDER_PER_PAGE,
  }

  let titleColor
  switch (theme) {
    case 'fox1':
    case 'homes1':
      titleColor = 'accent2'
      break
    case 'insider':
      titleColor = 'accent'
      break
    default:
      titleColor = 'secondary'
  }

  return (
    <div className={styles.mainContainer}>
      {showDisclosure && <AdvertiserDisclosure tooltipId="fox-disclosure" theme={theme === 'fox1' ? 'fox' : theme} />}

      <Testable className={styles.container} testId="rate-widget-container">
        <div className={styles.tooltip}>
          <Tooltip
            analyticsOptions={{ contentSubject: 'How we calculate rates tooltip' }}
            contentProps={{ type: 'disclaimer', color: 'lighter' }}
            triggerProps={{ type: 'bodySmall', color: titleColor }}
            triggerContent={<span>How we calculate rates</span>}
          >
          Top rates: After comparing our lenders’ pricing for today, we’ve pulled the option with
          the lowest points associated with each interest rate. To see all rates from our lenders,
          click on “All rates”.
          </Tooltip>
        </div>
        {loadingProducts && <div className={styles.overlay} />}
        <Testable className={styles.header} testId="rate-widget-header">
          <Font className={styles.title} type="medium" weight="bold">
            Today's Mortgage Rates
          </Font>
          <div className={styles.capture}>
            {editButton(isOpen)}
            <SortingForm
              initialValues={{ sorting }}
              className={`${styles.inputWrapper} ${isDesktop && styles.hidden} mt-24`}
            />
            <Collapse theme={{ collapse: 'collapseContainer' }} isOpened={isOpen}>
              <FilterForm {...filterFormProps} />
            </Collapse>
          </div>
          <SortingForm
            initialValues={{ sorting }}
            className={`${styles.inputWrapper} ${!isDesktop && styles.hidden}`}
          />
        </Testable>
        <TabsForm
          onGetStarted={onGetStarted}
          error={error}
          loading={loadingProducts}
          bestProducts={sortedBestProduct}
          allProducts={sortedProducts}
          isFullTable={isFullTable}
          analyticsContexts={analyticsContexts}
          isCashOutError={isCashOutError}
          paginationProps={paginationProps}
          activeTab={activeTab}
          theme={theme}
          isValidationsError={isValidationsError}
          isPurchase={isPurchase}
        />
        {isFullTable && !isErrorState && (
          <CompareRates onGetStarted={onGetStarted} analyticsContexts={analyticsContexts} />
        )}
        <RateWidgetFooter theme={theme} />
      </Testable>
    </div>
  )
}

Widget.propTypes = {
  variation: PropTypes.string.isRequired,
  theme: PropTypes.string.isRequired,
  trackSnowplowEvent: PropTypes.func.isRequired,
  analyticsContexts: PropTypes.arrayOf(
    PropTypes.shape({
      schema: PropTypes.string,
      data: PropTypes.object,
    }),
  ),
  formData: PropTypes.object,
  utms: PropTypes.object.isRequired,
  partnerFormData: PropTypes.object,
  initialValues: PropTypes.object,
  styles: PropTypes.object.isRequired,
  sortBy: PropTypes.string,
  termState: PropTypes.object,
  loanTypeState: PropTypes.oneOf([PURCHASE_TYPE, REFINANCE_TYPE]),
  isPurchase: PropTypes.bool,
  omitUtmParams: PropTypes.bool,
  page: PropTypes.number,
  metaParams: PropTypes.object,
  verticals: PropTypes.array,
  type: PropTypes.string,
  actions: PropTypes.shape({
    setSubmission: PropTypes.func.isRequired,
  }).isRequired,
}

Widget.defaultProps = {
  analyticsContexts: [],
  page: 0,
  formData: {},
  partnerFormData: undefined,
  initialValues: null,
  omitUtmParams: false,
}

const mapDispatchToProps = dispatch => ({
  actions: {
    setSubmission: bindActionCreators(setSubmissionAction, dispatch),
  },
})

const App = compose(
  withApolloProvider,
  withReduxProvider,
  withSnowplowContexts,
  withStyles(defaultStyles, { fox1, homes1, insider }),
  connect(mortgageWidgetMapStateToProps, mapDispatchToProps),
)(Widget)

export { Widget }
export default App
